import { cn } from "@corpuscomplete/web-ui/lib/utils";
import { Text } from "@corpuscomplete/web-ui/components/text";
import type { ReactNode, RefObject } from "react";

interface PendingActionCardProps {
  /** Icon to display in the header */
  icon?: ReactNode;
  /** Title text in the header */
  title: string;
  /** Additional content in the header (e.g., navigation controls) */
  headerRight?: ReactNode;
  /** Main scrollable content */
  children: ReactNode;
  /** Footer content (e.g., action buttons) */
  footer?: ReactNode;
  /** Height of the scrollable content area */
  contentHeight?: number;
  /** Ref for the scrollable content container */
  contentRef?: RefObject<HTMLDivElement | null>;
  /** Additional className */
  className?: string;
}

export function PendingActionCard({
  icon,
  title,
  headerRight,
  children,
  footer,
  contentHeight = 280,
  contentRef,
  className,
}: PendingActionCardProps) {
  return (
    <div
      className={cn(
        "fade-in slide-in-from-bottom-2 animate-in rounded-lg border border-primary/20 bg-primary/5 duration-200",
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-2 border-primary/10 border-b px-3 py-2">
        {icon}
        <Text variant="sm" className="font-medium text-primary">
          {title}
        </Text>
        {headerRight && <div className="ml-auto flex items-center">{headerRight}</div>}
      </div>

      {/* Scrollable content area */}
      <div ref={contentRef} className="overflow-y-auto px-3 py-3" style={{ height: contentHeight }}>
        {children}
      </div>

      {/* Footer */}
      {footer && (
        <div className="flex items-center justify-end gap-2 border-primary/10 border-t px-3 py-2">
          {footer}
        </div>
      )}
    </div>
  );
}
